<script lang="ts">
import BaseCard from '@/components/cards/new/BaseCard.vue'
import CardHeader from '@/components/cards/new/CardHeader.vue'
import MediaCard from '@/components/cards/new/MediaCard.vue'
import fetcher from '@/services/fetcher'
import { useInstantWinsStore } from '@/stores/instant-wins'
import { usePrizesStore } from '@/stores/prizes'
import type {
  EntrySummaryItem,
  EntrySummaryResponse,
  SwitchOptions,
} from '@/types'
import {
  DrawEntryTypes,
  Icons,
  InstantWinType,
  type TIconValue,
} from '@/utils/helper-objects'
import { toPortalUrl } from '@/utils/url-generation-utils'
import { groupBy } from '@/utils/utils'
import { type PropType, defineComponent } from 'vue'

/**
 * Represents a normalized entry item (Instant Win or Prize Draw) with it's
 * values resolved ready for rending
 */
type EntryItem = {
  group: string
  code: string
  entries: number
  image: string
  title: string
  date: string
  icon?: TIconValue
  drawType: string
  gradientColor1?: string
  gradientColor2?: string
  instantWinTypeId?: number
}

/**
 * Represents a group of normalized entry items (Instant Win or Prize Draw)
 */
type GroupedEntryItem = {
  title: string
  icon?: TIconValue
  totalEntryCount: number
  entries: EntryItem[]
}

// These are exported so the router can use them to set the `selected` prop
// from the URL hash. They're only used here so aren't polluting the types file.
export const EntryFilterType = Object.freeze({
  Subscriptions: 'subscriptions',
  InstantGames: 'instantgames',
})

export default defineComponent({
  components: {
    BaseCard,
    MediaCard,
    CardHeader,
  },
  props: {
    /**
     * Sets the default state of the entry filter control. Defaults to the
     * current subcription prize draws.
     */
    entryFilter: {
      type: String as PropType<
        (typeof EntryFilterType)[keyof typeof EntryFilterType]
      >,
      default: EntryFilterType.Subscriptions,
    },
  },
  setup() {
    return {
      instantWinsStore: useInstantWinsStore(),
      prizesStore: usePrizesStore(),
      options: [
        { label: 'Subscriptions', value: EntryFilterType.Subscriptions },
        { label: 'Instant Games', value: EntryFilterType.InstantGames },
      ] as Array<SwitchOptions>,
    }
  },
  data() {
    return {
      entries: [] as EntrySummaryItem[],
      selectedType: this.entryFilter,
      isLoading: false,
    }
  },
  computed: {
    filteredEntries() {
      const filteredDrawType =
        this.selectedType === EntryFilterType.Subscriptions
          ? 'subscription'
          : 'purchase'
      return this.formatData(
        this.entries.filter((entry) => entry.drawType === filteredDrawType)
      )
    },
  },
  async mounted() {
    await this.fetchData()
  },
  methods: {
    /**
     * Handles the change event for the Subscription/InstantGames switch. When
     * called, this handler will update the URL hash allowing the page to
     * restore the switch state. Without this, the component will always mount
     * with the subscription tag active.
     */
    async handleSwitchChange() {
      await this.$router.replace({
        hash: `#${this.selectedType}`,
      })
    },
    /**
     * Requests entry records from the API.
     */
    async fetchData(): Promise<void> {
      try {
        this.isLoading = true
        const url = toPortalUrl('customers/tickets/draws')
        const { data } = await fetcher.get<EntrySummaryResponse>(url)
        const csi_content = data.value!.csi_content
        this.entries = csi_content
      } catch (err) {
        this.$alertStore.addErrorAlert(
          { message: 'Unable to load your entries', buttonText: 'Ok' },
          true
        )
        console.error(err)
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Maps the API game data into an array of entry properties mixed with meta
     * data from an instant game or prize draw. The new array is be used to
     * render a contextual preview of the entry to the user.
     */
    formatData(data: EntrySummaryItem[]) {
      return data.map((entry): EntryItem => {
        const item: EntryItem = {
          group: 'Other',
          code: entry.drawCode,
          entries: entry.entryCount,
          image: '',
          title: entry.drawCode,
          date: 'N/A',
          drawType: entry.drawType,
        }

        switch (entry.drawType) {
          case DrawEntryTypes.Subscription: {
            const prize = this.prizesStore.prizesArray.find(
              (item) => item.code === entry.drawCode
            )
            if (prize) {
              item.group = 'Member Prizes'
              item.icon = Icons.Clover
              item.code = prize.code
              item.image = prize.card_image.src
              item.title = prize.title
              item.date = this.$d(prize.drawn_at.toJSDate(), 'long')
            }
            break
          }

          case DrawEntryTypes.Purchase: {
            const game = this.instantWinsStore.allInstantWinDrawArray.find(
              (item) => item.code === entry.drawCode
            )
            if (game) {
              item.group = game.__type
              item.code = game.code
              // Use card_image for mystery boxes and instant packs, regular image for instant wins and spins
              item.image =
                game.instant_win_type_id === InstantWinType.MysteryBox ||
                game.instant_win_type_id === InstantWinType.InstantPack
                  ? game.main_prize.card_image.src
                  : game.image.src
              item.title = game.title
              item.date = this.$d(game.main_prize.drawn_at.toJSDate(), 'long')
              item.gradientColor1 = game.appearance?.background_gradient_color_1
              item.gradientColor2 = game.appearance?.background_gradient_color_2
              item.instantWinTypeId = game.instant_win_type_id

              switch (game.instant_win_type_id) {
                case InstantWinType.InstantSpin:
                  item.group = 'Prize Spins'
                  item.icon = Icons.SlotMachine
                  break
                case InstantWinType.MysteryBox:
                  item.group = 'Mystery Boxes'
                  item.icon = Icons.Present
                  break
                case InstantWinType.Standard:
                  item.group = 'Instant Prizes'
                  item.icon = Icons.Lightening
                  break
                case InstantWinType.InstantPack:
                  item.group = 'Instant Packs'
                  item.icon = Icons.CardPack
                  break
              }
            }
            break
          }
        }
        return item
      })
    },
    /**
     * Converts groups a flat array of entry items by their `.group` key,
     * allowing them to be rendered together
     */
    groupItems(items: EntryItem[]): GroupedEntryItem[] {
      return Object.entries(groupBy((item) => item.group, items))
        .map(([title, entries]) => ({
          title,
          icon: entries[0].icon,
          totalEntryCount: entries.reduce(
            (partialSum, a) => partialSum + a.entries,
            0
          ),
          entries,
        }))
        .sort((a, b) => a.title.localeCompare(b.title))
    },
  },
})
</script>

<template>
  <AppPageTitle :previous-route="{ name: 'members-zone' }" />
  <AppSection>
    <AppSwitch
      v-model="selectedType"
      name="entryType"
      :options
      class="mx-auto mb-4 w-full max-w-md"
      @change="handleSwitchChange"
    />
    <div v-if="isLoading" class="flex justify-center items-center flex-col">
      <div class="spinner h-8 w-8"></div>
      <p>Loading your entries...</p>
    </div>

    <template v-if="!isLoading">
      <div v-if="filteredEntries.length" class="flex flex-col gap-5">
        <BaseCard
          v-for="(group, groupIndex) in groupItems(filteredEntries)"
          :key="group.title"
        >
          <AppAccordionSingle open :name="`entry-group-${groupIndex}`">
            <template #title>
              <CardHeader :title="group.title">
                <template v-if="group.icon" #start>
                  <AppIcon :icon="group.icon" />
                </template>
                <template #end>
                  {{ $n(group.totalEntryCount) }} Entries
                </template>
              </CardHeader>
            </template>
            <template #text>
              <!-- TODO: switch this wrapper for `<CardGrid>` when it uses container queries -->
              <div class="@container/grid">
                <div
                  class="grid grid-cols-1 gap-4 @xs/grid:grid-cols-2 @md/grid:grid-cols-3 @lg/grid:grid-cols-4 mt-4 -mb-4"
                >
                  <MediaCard v-for="(entry, i) of group.entries" :key="i">
                    <template #media>
                      <div
                        v-if="
                          entry.drawType === 'purchase' &&
                          (entry.instantWinTypeId === 2 ||
                            entry.instantWinTypeId === 4)
                        "
                        class="aspect-5/4 w-full relative overflow-hidden before:h-full"
                        :style="{
                          '--angled-bg-gradient-end':
                            entry.gradientColor1 ?? '#8C78FF',
                          '--angled-bg-gradient-start':
                            entry.gradientColor2 ?? '#082330',
                          '--angled-bg-right-height': '35%',
                          '--angled-bg-left-height': '70%',
                        }"
                      >
                        <img
                          :src="entry.image"
                          alt=""
                          class="aspect-5/4 w-full angled-bg object-contain p-3 z-10"
                        />
                      </div>
                      <img
                        v-else
                        :src="entry.image"
                        alt=""
                        :class="[
                          'aspect-5/4 w-full',
                          entry.drawType === 'purchase'
                            ? ['bg-slate-100 object-contain']
                            : 'object-cover',
                        ]"
                      />
                      <div
                        class="w-full bg-primary p-1 px-8 text-center font-bold text-white"
                      >
                        {{ entry.date }}
                      </div>
                    </template>
                    <template #default>
                      <CardHeader
                        :title="entry.title"
                        :subtitle="`${$n(entry.entries)} Entries`"
                      />
                    </template>
                    <template #actions>
                      <AppButton
                        :route="{
                          name: 'members-zone-entries-detail',
                          params: { code: entry.code },
                        }"
                      >
                        View Entries
                      </AppButton>
                    </template>
                  </MediaCard>
                </div>
              </div>
            </template>
          </AppAccordionSingle>
        </BaseCard>
      </div>
      <div v-else-if="!isLoading" class="flex flex-col items-center gap-4">
        <AppIcon :icon="Icons.Entries" size="lg" />
        <h2 class="text-2xl font-bold">No Entries</h2>
        <p>No problem! Why not try one of our games?</p>
        <div class="flex flex-col gap-4">
          <AppButton
            class="button-primary"
            :route="{
              name: 'prizes',
              query: { tab: 'mystery-boxes', switch: 'instant-wins' },
            }"
          >
            Play Mystery Boxes
          </AppButton>
          <AppButton
            class="bg-primary"
            :route="{
              name: 'prizes',
              query: { tab: 'instant-spins', switch: 'instant-wins' },
            }"
          >
            Play Instant Wins
          </AppButton>
        </div>
      </div>
    </template>
  </AppSection>
</template>
